import {Injectable} from '@angular/core';

import {LoginRestService} from './login-rest.service';
import {PromiseService, SessionStorageService} from 'ac-infra';
import {WebSocketService} from '../../common/services/ws/ws.service';
import {Router} from '@angular/router';
import {SessionRestService} from './session-rest.service';
import {RestResponseSuccess} from '../../common/server-actions/rest';
import {Actions} from '../../common/server-actions/actions';
import {UpdatePasswordDialogComponent} from '../../dialogs/update-password-dialog/update-password-dialog.component';
import {ServerDownDialogComponent} from '../../dialogs/server-down-dialog/server-down-dialog.component';
import {NavigationService} from '../../common/utilities/navigation.service';
import {SessionService} from '../../common/services/session.service';
import {ServerInfoService} from '../../common/services/server-info.service';
import {
    MultiLoginOperatorDialogComponent
} from '../../dialogs/multi-login-operator-dialog/multi-login-operator-dialog.component';
import {GroupsActions} from "../../network/services/actions/groups-actions.service";
import {cloneDeep} from "lodash";
import {PENDING_MFA_SESSION_KEY, PREFIX_SALT} from '../../models/session.models';

export interface LoginCredentials {
    username: string;
    password?: string;
    redirect?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class LoginService extends Actions {
    MFASessionId;
    MFAType: string;

    constructor(private loginRestService: LoginRestService,
                private groupsActions: GroupsActions,
                private sessionService: SessionService,
                private webSocketService: WebSocketService,
                private router: Router,
                private sessionRestService: SessionRestService,
                private navigationService: NavigationService,
    ) {
        super({});
    }

    login = (credentials: LoginCredentials) => {
        const defer = PromiseService.defer();
        const success = (response) => {
            defer.resolve();

            if (response.status === 202 && (response.data.operatorAuthenticationMode === 'AAD' || response.data.operatorAuthenticationMode === 'IAM')) {
                this.handleMFAWindow(response);
            } else {
                if (response.data.passwordExpirationDaysLeft < 0) {
                    this.saveLoggedUserAndStartSession(response.data);
                    return;
                }

                let message = 'Password will expire ';
                message += response.data.passwordExpirationDaysLeft === 0 ? 'today' : 'in ' + response.data.passwordExpirationDaysLeft + ' days';

                this.acDialogService.info(message, {onClose: () => this.saveLoggedUserAndStartSession(response.data)});
            }
        };

        const failure = (error) => {
            defer.reject(error);
            if (error) {
                this.handleErrorStatus(credentials.username, credentials.password, error.status);
            }
        };
        let body;
        if (credentials.redirect && ServerInfoService.serverInfo.whiteLabeling?.id) {
            body = {
                id: ServerInfoService.serverInfo.whiteLabeling?.id,
                type: ServerInfoService.serverInfo.whiteLabeling?.type
            };
        }
        this.loginRestService.login({
            username: credentials.username,
            password: credentials.password,
            body,
            success,
            failure
        });
        return defer.promise;
    };

    logout = () => {
        const successMessage = 'User logout successfully';
        const failureMessage = 'Failed to logout successfully';

        const onSuccess = () => {
            this.navigationService.goTo('/login');
        };
        const onFailure = (error) => {
            this.navigationService.goTo('/login');
        };

        this.webSocketService.disconnect(true, true);
        this.generalService.resetLoading();
        if (this.sessionService.activeSession) {
            this.loginRestService.logout(onSuccess, onFailure);
            this.sessionService.activeSession = undefined;
        } else {
            SessionStorageService.clearAllData();
            onSuccess();
        }
    };

    openMFALoginPage = () => {
        const success = (response: RestResponseSuccess) => {
            this.handleMFAWindow(response);
        };

        let dataObject: any;
        const whiteLabeling = ServerInfoService.serverInfo.whiteLabeling;

        if (whiteLabeling && whiteLabeling.id && whiteLabeling.type) {
            dataObject = {id: whiteLabeling.id, type: whiteLabeling.type};
        }
        this.loginRestService.add(success, () => null, dataObject, 'security/actions/login', undefined, undefined, false);
    };

    handleMFAWindow = (response) => {
        this.MFAType = response.data.operatorAuthenticationMode;
        this.MFASessionId = response.data.sessionId;

        this.savePendingMFASession(response.data.sessionId);
        this.redirectToMFAProvider(response);
    };

    channelApprovedCommitment = (session) => {
        const onSuccess = () => {
            this.getSession(session.sessionId);
        }
        this.loginRestService.put(onSuccess, this.logout, undefined,'security/actions/approveCommitment');
    }

    openServiceCommercialInfoDialog = (response) => {
        this.groupsActions.openServiceCommercialInfoDialog({
            commitmentFileId: response?.data?.commitmentFileId,
            isLogin: true,
            callback: this.channelApprovedCommitment,
            session: response?.data,
            closeCallback: this.logout
        });
    }

    getSession = (sessionId) => {
        const success = (response: RestResponseSuccess) => {
            if(response?.data?.isCommitmentEnabled && response?.data?.isCommitmentApproved === false){
               this.openServiceCommercialInfoDialog(response);
            } else if (response?.data?.multiLogin?.length > 0) {
                this.handleMultiLoginOperator(response.data.multiLogin);
            } else if (response.data.securityLevel) {
                this.saveLoggedUserAndStartSession(response.data);
            } else {
                this.generalService.loginErrorMessage = 'Login Failed';
                this.logout();
            }
        };

        const failure = () => {
            this.logout();
        };

        return this.sessionRestService.getById({
            id: sessionId,
            success,
            failure,
        });
    };

    handleMultiLoginOperator = (operators) => {
        const dialogData: any = {entities: operators};

        this.acDialogService.open(MultiLoginOperatorDialogComponent, {
            onSubmit: () => {
                const operator = cloneDeep(dialogData.entity);
                delete operator.logo;

                const onSuccess = (response) => {
                    if(response?.data?.isCommitmentEnabled && response?.data?.isCommitmentApproved === false){
                        this.openServiceCommercialInfoDialog(response);
                    }else{
                        this.saveLoggedUserAndStartSession(response?.data);
                    }
                }

                this.loginRestService.add(onSuccess, this.logout, operator, 'security/actions/oclogin');
            },
            onCancel: () => this.logout(),
            dialogData,
        });
    };

    savePendingMFASession = (sessionId: string) => {
        const encryptedSession = btoa(PREFIX_SALT + sessionId);
        localStorage.setItem(PENDING_MFA_SESSION_KEY, encryptedSession);
    };

    redirectToMFAProvider = (response) => {
        const location = response.headers.get('Location');
        this.generalService.MFAInProgress = true;
        window.location.href = location;
    };

    getPendingMFASession = (): string | null => {
        const encryptedSession = localStorage.getItem(PENDING_MFA_SESSION_KEY);
        if (!encryptedSession) {
            return null;
        }
        try {
            return atob(encryptedSession).replace(PREFIX_SALT, '');
        } catch (err) {
            return null;
        }
    };

    clearPendingMFASession = () => {
        localStorage.removeItem(PENDING_MFA_SESSION_KEY);
    };

    handleMFARedirectReturn = async () => {
        const sessionId = this.getPendingMFASession();
        if (sessionId) {
            this.clearPendingMFASession();
            this.generalService.MFAInProgress = false;
            this.sessionService.activeSession = {sessionId};
            return await this.getSession(sessionId);
        }
        return false;
    };

    ngOnDestroy() {
    }

    saveLoggedUserAndStartSession = (userDetails, sessionRestored = false) => {
        this.sessionService.startSession(userDetails);
        if (!sessionRestored) {
            this.router.navigateByUrl('');
        }
    };

    handleErrorStatus = (username, password, status) => {
        if (status === 403) {
            this.updateOperatorPassword(username, password);
        } else if (status === 503) {
            this.acDialogService.open(ServerDownDialogComponent);
        }
    };

    updateOperatorPassword = (operatorName, operatorPassword = '') => {
        const successMessage = 'Password for operator "' + operatorName + '" was updated successfully';
        const failureMessage = 'Failed to update password for operator "' + operatorName + '"';


        const onSuccessCallback = () => this.login({
            username: dialogData.entity.operatorName,
            password: dialogData.entity.newPassword
        });
        const onFailureCallback = (error) => this.acDialogService.fail(error.data.description);

        const dialogData = {
            entity: {operatorName, oldPassword: operatorPassword, newPassword: '', confirmNewPassword: ''},
            dontPerformRefreshOnSuccess: true,
            successMessage,
            failureMessage,
            onSuccessCallback,
            onFailureCallback
        };
        const serverCallback = (onSuccess, onFailure) => this.loginRestService.updateOperatorPassword(dialogData.entity, onSuccess, onFailure);
        this.genericAction({
            dialogComponentType: UpdatePasswordDialogComponent,
            serverCallback,
            dialogData,
        });
    };
}
